'use client';
import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';

const CircularGallery = dynamic(() => import('./CircularGallery'), { ssr: false });

const speciesItems = [
  { image: '/assets/images/CleanShot_2026-05-30_at_18.01.45_2x-1780182158450.png', commonName: 'Eastern Cottontail', scientific: 'Sylvilagus floridanus', handle: 'arielprimo', park: 'Old Renner Park', count: 10, badge: '' },
  { image: '/assets/images/CleanShot_2026-05-30_at_17.59.19_2x-1780182067436.png', commonName: 'Red Imported Fire Ant', scientific: 'Solenopsis invicta', handle: 'arielprimo', park: 'Old Renner Park', count: 11, badge: 'TOP INVASIVE' },
  { image: '/assets/images/CleanShot_2026-05-30_at_17.59.06_2x-1780182092378.png', commonName: 'Sugar Hackberry', scientific: 'Celtis laevigata', handle: 'danielksa', park: 'Preston Green Park', count: 13, badge: '' },
  { image: '/assets/images/CleanShot_2026-05-30_at_17.57.52_2x-1780181890374.png', commonName: 'Baldcypress', scientific: 'Taxodium distichum', handle: 'jonathansharvit', park: 'Bentwood Park', count: 11, badge: '' },
  { image: '/assets/images/CleanShot_2026-05-30_at_18.01.57_2x-1780182169001.png', commonName: 'Eastern Fox Squirrel', scientific: 'Sciurus niger', handle: 'shaunnadato12', park: 'Lively Pointe Park', count: 5, badge: '' },
  { image: '/assets/images/CleanShot_2026-05-30_at_17.56.49_2x-1780181856727.png', commonName: 'Straggler Daisy', scientific: 'Calyptocarpus vialis', handle: 'b3nbern1216', park: 'Shady Grove Park', count: 21, badge: 'MOST OBSERVED' },
  { image: '/assets/images/CleanShot_2026-06-01_at_22.05.16_2x-1780369588109.png', commonName: 'Quihoui Privet', scientific: 'Ligustrum quihoui', handle: 'ariel94969', park: 'Churchill Park', count: 10, badge: '' },
  { image: '/assets/images/CleanShot_2026-06-01_at_22.08.14_2x-1780369978227.png', commonName: "Doubtful Knight\'s Spur", scientific: 'Delphinium ajacis', handle: 'idan_rahamim', park: 'Cottonwood Park', count: 3, badge: '' },
  { image: '/assets/images/CleanShot_2026-06-01_at_22.09.39_2x-1780370023119.png', commonName: 'Mallard', scientific: 'Anas platyrhynchos', handle: 'jacob25047', park: 'Curtis Park', count: 5, badge: '' },
  { image: '/assets/images/CleanShot_2026-06-01_at_22.09.50_2x-1780370100004.png', commonName: 'Carolina Snailseed', scientific: 'Nephroia carolina', handle: 'jonathansharvit', park: 'Bentwood Park', count: 7, badge: '' },
  { image: '/assets/images/CleanShot_2026-06-01_at_22.09.56_2x-1780370175548.png', commonName: 'Southern Magnolia', scientific: 'Magnolia grandiflora', handle: 'arielprimo', park: 'Old Renner Park', count: 2, badge: '' },
  { image: '/assets/images/CleanShot_2026-06-01_at_22.10.11_2x-1780370391341.png', commonName: 'Common Hedge Parsley', scientific: 'Torilis arvensis', handle: 'danielksa', park: 'Preston Green Park', count: 8, badge: '' },
];

const galleryItems = speciesItems.map((s) => ({ image: s.image, text: s.commonName }));

function getBend(width: number): number {
  if (width < 480) return 1;
  if (width < 768) return 2;
  return 3;
}

export default function FieldGallerySection() {
  const [bend, setBend] = useState(3);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const update = () => setBend(getBend(window.innerWidth));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const current = speciesItems[active] ?? speciesItems[0];

  return (
    <section
      id="gallery"
      style={{
        backgroundColor: 'var(--paper)',
        scrollMarginTop: '96px',
        paddingTop: '120px',
        paddingBottom: '140px',
        paddingLeft: '24px',
        paddingRight: '24px',
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* ── Section Header ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            alignItems: 'end',
          }}
        >
          {/* Left column: eyebrow + headline */}
          <div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                borderRadius: '9999px',
                backgroundColor: 'var(--cream)',
                border: '1px solid rgba(16,46,29,0.12)',
                marginBottom: '16px',
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--moss)',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.35em',
                  color: 'var(--moss)',
                }}
              >
                FIELD GALLERY · MAY 2026
              </span>
            </div>

            <h2
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: 'clamp(2rem, 5vw, 3.8rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                fontWeight: 500,
                fontVariationSettings: "'opsz' 144",
                color: 'var(--ink)',
                margin: 0,
              }}
            >
              Species we{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--moss)' }}>found.</em>
            </h2>
          </div>

          {/* Right column: intro paragraph */}
          <div>
            <p
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: '17px',
                lineHeight: 1.65,
                color: 'var(--ash)',
                maxWidth: '60ch',
                margin: 0,
              }}
            >
              Every photograph in the inventory was taken by a student researcher in the field and verified by the global iNaturalist community. Twelve standout finds from Year 1 — across plants, insects, birds, mammals, and the canopy that holds them together.
            </p>
          </div>
        </div>

        {/* ── Active Species Info Card ── */}
        <div
          style={{
            marginTop: '64px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'relative',
              padding: '24px 32px',
              borderRadius: '12px',
              backgroundColor: 'var(--cream)',
              border: '1px solid rgba(16,46,29,0.10)',
              minWidth: '280px',
              maxWidth: '480px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            {current.badge ? (
              <div
                style={{
                  display: 'inline-flex',
                  marginBottom: '12px',
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '9px',
                    fontWeight: 700,
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.2em',
                    color: '#ffffff',
                    backgroundColor: current.badge === 'TOP INVASIVE' ? '#B5582E' : 'var(--moss)',
                    padding: '3px 10px',
                    borderRadius: '9999px',
                  }}
                >
                  {current.badge}
                </span>
              </div>
            ) : null}
            <p
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
                fontWeight: 500,
                color: 'var(--ink)',
                margin: '0 0 4px 0',
                lineHeight: 1.2,
              }}
            >
              {current.commonName}
            </p>
            <p
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '12px',
                fontStyle: 'italic',
                color: 'var(--ash)',
                margin: '0 0 12px 0',
                letterSpacing: '0.02em',
              }}
            >
              {current.scientific}
            </p>
            <p
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '11px',
                fontWeight: 700,
                color: 'var(--moss)',
                margin: '0 0 6px 0',
                letterSpacing: '0.1em',
                textTransform: 'uppercase' as const,
              }}
            >
              {current.count !== null ? `${current.count} observations` : '— observations'}
            </p>
            <p
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '10px',
                color: 'var(--ash)',
                margin: 0,
                letterSpacing: '0.05em',
                fontStyle: 'italic',
              }}
            >
              Observed by @{current.handle} at {current.park}
            </p>
            <div
              style={{
                marginTop: '16px',
                height: '1px',
                backgroundColor: 'rgba(16,46,29,0.08)',
              }}
            />
          </div>
        </div>

        {/* ── CircularGallery ── */}
        <div
          style={{
            marginTop: '48px',
            width: '100vw',
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',
            height: 'clamp(380px, 50vw, 600px)',
            backgroundColor: 'var(--paper)',
          }}
        >
          <CircularGallery
            key={galleryItems.map((s) => s.image).join('|')}
            items={galleryItems}
            bend={bend}
            textColor="#102E1D"
            borderRadius={0.06}
            font="bold 16px 'Fraunces', serif"
            scrollEase={0.05}
            scrollSpeed={2}
            onActiveChange={setActive}
          />
        </div>

        {/* ── Caption row ── */}
        <div
          style={{
            marginTop: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '12px',
              fontWeight: 400,
              textTransform: 'uppercase' as const,
              letterSpacing: '0.2em',
              color: 'var(--ash)',
            }}
          >
            DRAG TO EXPLORE · 12 SPECIES · YEAR 1 INVENTORY
          </span>
        </div>
      </div>
    </section>
  );
}
